<template>
	<div v-html="svg" :style="{ width: size+'px', height: size+'px', display: display }">
	</div>
</template>

<script setup lang="ts">
import axios from 'axios'
import { computed, ref, watch } from 'vue'
import { sanitizeHtml } from '@/utilities/sanitize'

interface Props {
	icon?: string
	size: string
}

const props = defineProps<Props>()
const svg = ref('')

// Cache for loaded SVGs to prevent duplicate requests
const svgCache = new Map<string, string>()

async function load() {
	if (props.icon) {
		// Check cache first
		const cached = svgCache.get(props.icon)
		if (cached) {
			svg.value = cached
			return
		}

		try {
			const res = await axios.get(`/country/${props.icon}.svg`)

			// Sanitize SVG content to prevent XSS
			const sanitizedSvg = sanitizeHtml(res.data, {
				allowedTags: ['svg', 'path', 'circle', 'rect', 'g', 'defs', 'clipPath'],
				allowedAttributes: {
					svg: ['xmlns', 'viewBox', 'width', 'height', 'fill'],
					path: ['d', 'fill', 'stroke', 'stroke-width'],
					circle: ['cx', 'cy', 'r', 'fill', 'stroke'],
					rect: ['x', 'y', 'width', 'height', 'fill', 'stroke'],
					g: ['fill', 'stroke', 'transform'],
					clipPath: ['id'],
					defs: []
				}
			})

			// Cache the sanitized SVG
			svgCache.set(props.icon, sanitizedSvg)
			svg.value = sanitizedSvg
		}
		catch (_) {
			// Cache empty result to prevent repeated failed requests
			svgCache.set(props.icon, '')
			svg.value = ''
		}
	} else {
		svg.value = ''
	}
}

const display = computed(() => { return svg.value === '' ? 'none' : 'block' })

load()

watch(() => props.icon, () => load())
</script>
